/**
 * Rich text helpers — question labels store HTML; plain strings remain valid.
 */

function decodeBasicEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function stripTagsToText(html) {
  return decodeBasicEntities(
    String(html || '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<\/div>/gi, ' ')
      .replace(/<[^>]+>/g, '')
  );
}

export function stripHtmlToText(html) {
  return stripTagsToText(html).replace(/\s+/g, ' ').trim();
}

/** Plain-text preview for inputs — keeps spaces while typing (no trim/collapse). */
export function htmlToPlainLabel(html) {
  return stripTagsToText(html);
}

export function labelPlainInputValue(label) {
  if (!label) return '';
  return looksLikeHtml(label) ? htmlToPlainLabel(label) : String(label);
}

export function richTextIsEmpty(html) {
  return stripHtmlToText(html).length === 0;
}

export function looksLikeHtml(value) {
  return /<[a-z][\s\S]*>/i.test(String(value || ''));
}

const ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'P',
  'BR',
  'UL',
  'OL',
  'LI',
  'A',
  'SPAN',
  'DIV'
]);

export function sanitizeRichTextHtml(html) {
  const raw = String(html || '').trim();
  if (!raw) return '';

  if (typeof DOMParser === 'undefined') {
    return raw
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/\s on\w+="[^"]*"/gi, '')
      .replace(/\s on\w+='[^']*'/gi, '')
      .trim();
  }

  const doc = new DOMParser().parseFromString(raw, 'text/html');
  const walk = (node) => {
    const children = [...node.childNodes];
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName;
        if (!ALLOWED_TAGS.has(tag)) {
          const fragment = doc.createDocumentFragment();
          while (child.firstChild) fragment.appendChild(child.firstChild);
          child.replaceWith(fragment);
          walk(node);
          return;
        }
        [...child.attributes].forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (name.startsWith('on')) {
            child.removeAttribute(attr.name);
            return;
          }
          if (tag === 'A' && name === 'href') {
            const href = String(attr.value || '').trim();
            if (!/^https?:\/\//i.test(href) && !/^mailto:/i.test(href)) {
              child.removeAttribute(attr.name);
            }
            return;
          }
          if (name !== 'href' && name !== 'target' && name !== 'rel') {
            child.removeAttribute(attr.name);
          }
        });
        if (tag === 'A') {
          child.setAttribute('rel', 'noopener noreferrer');
          child.setAttribute('target', '_blank');
        }
      }
      if (child.nodeType === Node.ELEMENT_NODE || child.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        walk(child);
      }
    }
  };

  walk(doc.body);
  return doc.body.innerHTML.trim();
}
